import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

const WAITLIST_API_URL = import.meta.env.VITE_WAITLIST_API_URL || 'https://quljtpinne.execute-api.us-east-1.amazonaws.com/prod';

export default function ComingSoon() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${WAITLIST_API_URL}/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(data.message || "Thanks! We'll notify you when we launch.");
        setIsSuccess(true);
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Unable to submit. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="coming-soon">
      <style>{`
        .coming-soon {
          --charcoal: #2D2D2D;
          --rust: #C17C60;
          --cream: #FAF7F2;
          --sage: #7A9E7E;
          --tan: #D4C4B0;
          --warm-white: #FFFDF9;

          font-family: 'DM Sans', sans-serif;
          background: var(--charcoal);
          color: var(--warm-white);
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          position: relative;
          overflow: hidden;
        }

        .coming-soon::before {
          content: '';
          position: absolute;
          top: -50%;
          left: 50%;
          transform: translateX(-50%);
          width: 150%;
          height: 150%;
          background: radial-gradient(ellipse at center, var(--sage) 0%, transparent 70%);
          opacity: 0.1;
          pointer-events: none;
        }

        .coming-soon-container {
          text-align: center;
          max-width: 600px;
          position: relative;
          z-index: 1;
        }

        .coming-soon-logo {
          font-family: 'DM Sans', sans-serif;
          font-size: 2.5rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          margin-bottom: 3rem;
        }

        .coming-soon-title {
          font-family: 'DM Serif Display', serif;
          font-size: clamp(2.5rem, 8vw, 4rem);
          line-height: 1.1;
          margin-bottom: 1.5rem;
          font-weight: 400;
        }

        .coming-soon-title em {
          color: var(--rust);
          font-style: italic;
        }

        .coming-soon-subtitle {
          font-size: 1.125rem;
          color: var(--tan);
          margin-bottom: 3rem;
          line-height: 1.6;
        }

        .notify-form {
          display: flex;
          gap: 0.75rem;
          max-width: 450px;
          margin: 0 auto 2rem;
        }

        .notify-form input {
          flex: 1;
          padding: 1rem 1.25rem;
          border: none;
          background: var(--warm-white);
          color: var(--charcoal);
          font-size: 1rem;
          font-family: inherit;
        }

        .notify-form input::placeholder {
          color: var(--charcoal);
          opacity: 0.5;
        }

        .notify-form input:focus {
          outline: 2px solid var(--rust);
          outline-offset: 2px;
        }

        .notify-form button {
          padding: 1rem 2rem;
          background: var(--rust);
          color: var(--warm-white);
          border: none;
          font-size: 1rem;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          transition: opacity 0.2s;
          white-space: nowrap;
        }

        .notify-form button:hover:not(:disabled) {
          opacity: 0.9;
        }

        .notify-form button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .features {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 2rem;
          margin-top: 4rem;
          padding-top: 3rem;
          border-top: 1px solid rgba(255,255,255,0.1);
        }

        .feature {
          text-align: center;
        }

        .feature-icon {
          width: 48px;
          height: 48px;
          background: var(--sage);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 0.75rem;
          font-size: 1.25rem;
        }

        .feature-label {
          font-size: 0.875rem;
          color: var(--tan);
        }

        .coming-soon-footer {
          position: absolute;
          bottom: 2rem;
          left: 50%;
          transform: translateX(-50%);
          text-align: center;
        }

        .footer-links {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          margin-bottom: 0.75rem;
        }

        .footer-links a {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.4);
          text-decoration: none;
          transition: color 0.2s;
        }

        .footer-links a:hover {
          color: var(--tan);
        }

        .footer-copyright {
          font-size: 0.875rem;
          color: rgba(255,255,255,0.4);
        }

        .success-message {
          background: var(--sage);
          color: var(--warm-white);
          padding: 1rem 1.5rem;
          margin-bottom: 2rem;
          font-weight: 500;
        }

        .error-message {
          background: #dc2626;
          color: white;
          padding: 1rem 1.5rem;
          margin-bottom: 2rem;
          font-weight: 500;
        }

        @media (max-width: 600px) {
          .notify-form {
            flex-direction: column;
          }

          .features {
            gap: 1.5rem;
          }
        }
      `}</style>

      <div className="coming-soon-container">
        <div className="coming-soon-logo">venndela</div>

        <h1 className="coming-soon-title">Something <em>exciting</em> is coming</h1>

        <p className="coming-soon-subtitle">
          The modern affiliate marketing platform that helps you turn customers into advocates.
          Be the first to know when we launch.
        </p>

        {isSuccess && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {!isSuccess && (
          <form className="notify-form" onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder="Enter your email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Notify Me'}
            </button>
          </form>
        )}

        <div className="features">
          <div className="feature">
            <div className="feature-icon">🔗</div>
            <div className="feature-label">Unique Referral Links</div>
          </div>
          <div className="feature">
            <div className="feature-icon">📊</div>
            <div className="feature-label">Real-time Analytics</div>
          </div>
          <div className="feature">
            <div className="feature-icon">💰</div>
            <div className="feature-label">Automated Payouts</div>
          </div>
          <div className="feature">
            <div className="feature-icon">⚡</div>
            <div className="feature-label">5-Minute Setup</div>
          </div>
        </div>
      </div>

      <footer className="coming-soon-footer">
        <div className="footer-links">
          <Link to="/legal/terms">Terms of Service</Link>
          <Link to="/legal/privacy">Privacy Policy</Link>
          <Link to="/legal/acceptable-use">Acceptable Use</Link>
        </div>
        <div className="footer-copyright">
          &copy; {new Date().getFullYear()} Venndela. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
